import { usePublicBootstrap } from '../../seo/PublicBootstrap.jsx';
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PublicShell, BottomNav, getSpotlightAccent } from '@mts/ui';
import Header from './Header';
import Footer from './Footer';
import { LayoutViewportProvider } from './LayoutViewportContext';
import { BottomNavProvider, useBottomNav } from './BottomNavContext';
import { usePublicTenant } from '../../hooks/usePublicTenant';
import { AppOverlayProvider } from '../overlay/AppOverlayProvider';
import AppOverlayHost from '../overlay/AppOverlayHost';

const useBrowserLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

const BOTTOM_NAV_ROUTES = [
  { key: 'home',    path: '/' },
  { key: 'explore', path: '/listings' },
  { key: 'deals',   path: '/offers' },
  { key: 'saved',   path: '/saved' },
  { key: 'more',    path: '/more' },
];

function getActiveNavKey(pathname) {
  // Exact match first, then prefix match (longest wins)
  const exact = BOTTOM_NAV_ROUTES.find(r => r.path === pathname);
  if (exact) return exact.key;
  const prefix = [...BOTTOM_NAV_ROUTES]
    .filter(r => r.path !== '/' && pathname.startsWith(r.path))
    .sort((a, b) => b.path.length - a.path.length)[0];
  return prefix?.key ?? 'home';
}

// Convert #RRGGBB → "R G B" channels for Tailwind v3 CSS-var opacity support
function hexToChannels(hex) {
  const m = /^#([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
  return m ? `${parseInt(m[1], 16)} ${parseInt(m[2], 16)} ${parseInt(m[3], 16)}` : null;
}

function PageShellContent({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { brandTheme, bodyFont, headingFont } = usePublicTenant();
  const bootstrap = usePublicBootstrap();
  const [isMobile, setIsMobile] = useState(() => {
    if (bootstrap || typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 767px)').matches;
  });

  const searchParams = new URLSearchParams(location.search);
  const isListingEmbed = searchParams.get('embed') === 'listing'
    && (location.pathname.startsWith('/business/') || location.pathname.startsWith('/listings/'));
  const isSingleListingRoute = location.pathname.startsWith('/business/') || location.pathname.startsWith('/listings/');

  useBrowserLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const handleMediaChange = (event) => {
      setIsMobile(event.matches);
    };

    setIsMobile(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleMediaChange);

    return () => {
      mediaQuery.removeEventListener('change', handleMediaChange);
    };
  }, []);

  const brandVars = {
    '--font-body': bodyFont || 'Inter, system-ui, sans-serif',
    '--font-heading': headingFont || 'Montserrat, Inter, system-ui, sans-serif',
    '--background': brandTheme?.background || '#F8FAFC',
    '--card': brandTheme?.card || '#FBFCFB',
    '--primary': brandTheme?.primary || '#1D4ED8',
    '--primary-hover': brandTheme?.primaryHover || '#1E40AF',
    '--primary-foreground': brandTheme?.primaryForeground || '#FFFFFF',
    '--primary-soft': brandTheme?.primarySoft || '#DBEAFE',
    '--secondary': brandTheme?.secondary || '#0F172A',
    '--secondary-hover': brandTheme?.secondaryHover || '#0C2555',
    '--secondary-foreground': brandTheme?.secondaryForeground || '#FFFFFF',
    '--secondary-soft': brandTheme?.secondarySoft || '#E2E8F0',
    '--surface': brandTheme?.surface || '#FFFFFF',
    '--spotlight': brandTheme?.spotlight || getSpotlightAccent(brandTheme?.primary || '#1D4ED8').spotlightHex,
    '--spotlight-foreground': brandTheme?.spotlightForeground || '#0F172A',
    '--spotlight-soft': brandTheme?.spotlightSoft || '#FEF3C7',
    '--border': brandTheme?.border || '#E2E8F0',
    '--text': brandTheme?.text || '#0F172A',
    '--muted': brandTheme?.muted || '#64748B',
    '--hero-heading-color': brandTheme?.text || '#0F172A',
    '--hero-body-color': brandTheme?.muted || '#64748B',
    '--hero-overlay-color': brandTheme?.surface || '#F8FAFC',
    '--button-primary-bg': brandTheme?.primary || '#1D4ED8',
    '--button-primary-text': brandTheme?.primaryForeground || '#FFFFFF',
    '--button-primary-hover': brandTheme?.primaryHover || '#1E40AF',
    '--button-secondary-bg': brandTheme?.surface || '#FFFFFF',
    '--button-secondary-text': brandTheme?.primary || '#1D4ED8',
    '--button-secondary-border': brandTheme?.primarySoft || '#DBEAFE',
    '--button-ghost-text': brandTheme?.primary || '#1D4ED8',
    '--badge-basic-bg': brandTheme?.primarySoft || '#DBEAFE',
    '--badge-basic-text': brandTheme?.primary || '#1D4ED8',
    '--badge-featured-bg': brandTheme?.primary || '#1D4ED8',
    '--badge-featured-text': brandTheme?.primaryForeground || '#FFFFFF',
    '--badge-spotlight-bg': brandTheme?.spotlight || '#FFC107',
    '--badge-spotlight-text': brandTheme?.spotlightForeground || '#0F172A',
    // Design tokens exposed as RGB channels for Tailwind opacity-modifier support
    // RGB channel helpers derived from the brand tokens for Tailwind opacity modifiers.
    // Fallback defaults are set in packages/ui/src/styles/globals.css :root
    '--primary-rgb':       hexToChannels(brandTheme?.primary      || '#1D4ED8') || '29 78 216',
    '--primary-hover-rgb': hexToChannels(brandTheme?.primaryHover || '#1E40AF') || '30 64 175',
    '--primary-foreground-rgb': hexToChannels(brandTheme?.primaryForeground || '#FFFFFF') || '255 255 255',
    '--secondary-rgb':     hexToChannels(brandTheme?.secondary || '#0F172A') || '15 23 42',
    '--secondary-hover-rgb': hexToChannels(brandTheme?.secondaryHover || '#0C2555') || '12 37 85',
    '--secondary-foreground-rgb': hexToChannels(brandTheme?.secondaryForeground || '#FFFFFF') || '255 255 255',
    '--spotlight-rgb':     hexToChannels(brandTheme?.spotlight || '#F59E0B') || '245 158 11',
    '--spotlight-hover-rgb': hexToChannels(brandTheme?.spotlightHover || '#D97706') || '217 119 6',
    '--spotlight-foreground-rgb': hexToChannels(brandTheme?.spotlightForeground || '#111827') || '17 24 39',
    '--background-rgb':    hexToChannels(brandTheme?.background || '#F8FAFC') || '248 250 252',
    '--surface-rgb':       hexToChannels(brandTheme?.surface || '#FFFFFF') || '255 255 255',
    '--surface-alt-rgb':   hexToChannels(brandTheme?.surface || '#F8FAFC') || '248 250 252',
    '--border-rgb':        hexToChannels(brandTheme?.border || '#E2E8F0') || '226 232 240',
    '--text-rgb':  hexToChannels(brandTheme?.text || '#0F172A') || '15 23 42',
    '--body-rgb':     hexToChannels(brandTheme?.text || '#0F172A') || '15 23 42',
    '--muted-rgb':    hexToChannels(brandTheme?.muted  || '#64748B') || '100 116 139',
    '--success-rgb':       hexToChannels(brandTheme?.success || '#10B981') || '16 185 129',
    '--warning-rgb':       hexToChannels(brandTheme?.warning || '#F59E0B') || '245 158 11',
    '--danger-rgb':         hexToChannels(brandTheme?.danger || '#EF4444') || '239 68 68',
    // Hex vars for non-Tailwind direct usage (components using style={{ color: var(--page-body-color) }})
    '--page-heading-color': brandTheme?.text || '#0F172A',
    '--page-body-color':    brandTheme?.text || '#0F172A',
    '--ui-scrollbar-thumb': brandTheme?.primarySoft || '#DBEAFE',
    '--ui-scrollbar-track': brandTheme?.background || '#F8FAFC',
    '--ui-inline-edit-overlay': brandTheme?.text || '#0F172A',
    '--ui-input-border': brandTheme?.border || '#D1D5DB',
    '--ui-input-text': brandTheme?.text || '#0F172A',
    '--ui-card-border': brandTheme?.border || '#E2E8F0',
    '--ui-hero-backdrop-light': brandTheme?.surface || '#F8FAFC',
    '--ui-hero-backdrop-primary': brandTheme?.primary || '#1D4ED8',
    '--ui-hero-backdrop-secondary': brandTheme?.secondary || '#0F172A',
    '--ui-community-glow-blue': brandTheme?.primarySoft || '#DBEAFE',
    '--ui-community-glow-green': brandTheme?.secondarySoft || '#E2E8F0',
    '--ui-community-overlay': brandTheme?.text || '#0F172A',
    '--ui-category-fallback': brandTheme?.muted || '#6B7280',
    '--ui-category-tint': brandTheme?.primary || '#1D4ED8',
    '--badge-basic-color': brandTheme?.primarySoft || '#DBEAFE',
    '--badge-basic-text-color': brandTheme?.primary || '#1D4ED8',
    '--badge-featured-color': brandTheme?.primary || '#1D4ED8',
    '--badge-featured-text-color': brandTheme?.primaryForeground || '#FFFFFF',
    '--badge-spotlight-color': brandTheme?.spotlight || '#FFC107',
    '--badge-spotlight-text-color': brandTheme?.spotlightForeground || '#0F172A',
    '--success': brandTheme?.success || '#10B981',
    '--success-foreground': brandTheme?.successForeground || '#FFFFFF',
    '--warning': brandTheme?.warning || '#F59E0B',
    '--warning-foreground': brandTheme?.warningForeground || '#111827',
    '--danger': brandTheme?.danger || '#EF4444',
    '--danger-foreground': brandTheme?.dangerForeground || '#FFFFFF',
  };

  const handleBottomNavSelect = (key) => {
    const route = BOTTOM_NAV_ROUTES.find(r => r.key === key);
    if (route) navigate(route.path);
  };

  const { override: bottomNavOverride } = useBottomNav();

  return (
    <LayoutViewportProvider value={{ isMobile }}>
      <AppOverlayProvider>
        <div style={brandVars}>
          <PublicShell
            header={!isListingEmbed ? <Header isMobile={isMobile} /> : null}
            footer={!isListingEmbed ? <Footer isMobile={isMobile} /> : null}
          >
            {children}
          </PublicShell>
          {!isListingEmbed && (
            bottomNavOverride
              ? bottomNavOverride
              : !isSingleListingRoute && (
                  <BottomNav
                    activeKey={getActiveNavKey(location.pathname)}
                    onSelect={handleBottomNavSelect}
                  />
                )
          )}
          <AppOverlayHost />
        </div>
      </AppOverlayProvider>
    </LayoutViewportProvider>
  );
}

function PageShell({ children }) {
  return (
    <BottomNavProvider>
      <PageShellContent>{children}</PageShellContent>
    </BottomNavProvider>
  );
}

export { PageShell };
export default PageShell;
